// Helpers for treating a YouTube URL as an embed source rather than
// something to download and re-host. Shared between media-api (to validate
// a submitted URL and normalize an id) and media-site (to build the embed
// iframe's src from a video's stored source_url).

const YOUTUBE_ID_PATTERNS: RegExp[] = [
  /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/,
  /(?:youtu\.be\/)([\w-]{11})/,
  /(?:youtube\.com\/embed\/)([\w-]{11})/,
  /(?:youtube\.com\/shorts\/)([\w-]{11})/,
];

/** Extracts an 11-character YouTube video id from any common URL shape, or null if it doesn't look like one. */
export function extractYoutubeVideoId(url: string): string | null {
  for (const pattern of YOUTUBE_ID_PATTERNS) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
}

/**
 * Builds the embed src using only officially documented YouTube Player
 * Parameters (https://developers.google.com/youtube/player_parameters) that
 * reduce how much of the embed pulls a viewer toward YouTube itself, short
 * of anything that would require circumventing YouTube's own player:
 *   - youtube-nocookie.com: no tracking cookies until the visitor presses play
 *   - rel=0: when the video ends, only show suggestions from the *same*
 *     channel — YouTube removed the ability to disable "related videos"
 *     entirely in 2018, this is the closest still-supported option
 *   - modestbranding=1: minimizes (can't fully remove) YouTube branding
 *   - iv_load_policy=3: hides video annotations/cards
 *   - playsinline=1: plays inline on mobile Safari instead of forcing
 *     fullscreen takeover, so it reads as part of the page
 * `origin` (pass `window.location.origin` from a browser context) is
 * recommended by YouTube for embeds and required if the JS IFrame Player API
 * is ever added on top of this later.
 */
export function youtubeEmbedUrl(videoId: string, origin?: string): string {
  // Built by hand rather than via URLSearchParams — this package has no DOM
  // lib (it's used from both Workers and browser code), and every value
  // here is already a plain ASCII literal or a same-origin URL, so there's
  // nothing that actually needs percent-encoding except origin itself.
  const params = ["rel=0", "modestbranding=1", "iv_load_policy=3", "playsinline=1"];
  if (origin) params.push(`origin=${encodeURIComponent(origin)}`);
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.join("&")}`;
}

/** YouTube's own thumbnail CDN — always available, no API key needed. */
export function youtubeThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}
